import { Button } from "@/components/ui/button";
import { Lock, CheckCircle2, FileText } from "lucide-react";
import { dayStatusLabel, type DayStatus } from "@shared/trustLabels";
import { useI18n } from "@/lib/i18n";

/**
 * One owner-facing draft card (used for BOTH "Today" and "This week"). It is
 * purely presentational: the caller passes a SERVER-derived `status`
 * (`DayStatus`) — this component only maps that enum to a client-safe label +
 * dot colour (exactly like `WorkWeekStrip`) and picks the CTA. It computes NO
 * approval state, NO stale state, NO classification. "Private to you until you
 * approve & share" until the server says `approved`.
 */
const DOT: Record<DayStatus, string> = {
  approved: "bg-emerald-500",
  draft: "bg-sky-500",
  ready: "bg-violet-500",
  update_available: "bg-amber-500",
  needs_update: "bg-orange-500",
  retracted: "bg-slate-400",
  not_started: "bg-muted-foreground/40",
  weekend_note: "bg-amber-400",
  hidden: "bg-transparent",
};

export function DraftReceiptCard({
  label,
  dateLabel,
  status,
  headline,
  capturedCount = 0,
  busy,
  onDraft,
  onReview,
  onApprove,
  onUnshare,
  isPersonal = false,
}: {
  /** Section label, e.g. "Today" or "This week". */
  label: string;
  dateLabel?: string;
  /** SERVER-derived display status (from weekStatus / weekly status + flags). */
  status: DayStatus;
  headline?: string;
  /** Server `signalCount` for this day/week — shown on a not-started card so a
   *  captured-but-undrafted day is distinguishable from a truly empty one. */
  capturedCount?: number;
  busy?: boolean;
  onDraft: () => void;
  onReview: () => void;
  onApprove: () => void;
  /** Retract ("Unshare") — offered only while the team currently sees a
   *  snapshot. Silent takedown; the page hosts the confirm. */
  onUnshare?: () => void;
  /**
   * The receipt's workspace is a PERSONAL workspace (isPersonal=true). When
   * set, team-sharing language is dropped: approval finalizes the receipt for
   * the user only (no team audience), so "& share" / "Visible to team" /
   * "Unshare" are removed. Sharing MECHANICS are unchanged — copy + conditional
   * rendering only. Shareability is a property of the receipt's workspace.
   */
  isPersonal?: boolean;
}) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const approved = status === "approved";
  const updateAvailable = status === "update_available";
  const changesNotShared = status === "needs_update";
  const retracted = status === "retracted";
  const notStarted = status === "not_started";
  // REST DAYS (weekend_note / hidden) carry NO daily-receipt actions — same
  // rule as DailyReceiptsList ("rest days appear only as read-only captured
  // notes"). Before this guard they fell into the catch-all "never approved"
  // branch and rendered Review/Approve buttons for a day with NO receipt —
  // the Friday dead-button bug.
  const restDay = status === "weekend_note" || status === "hidden";
  // Post-approval states where the team is STILL seeing an approved snapshot
  // (retracted is the exception: the owner took it down). Drives the footer
  // copy and whether "Unshare" is offered.
  const hasApprovedSnapshot = approved || updateAvailable || changesNotShared;
  const statusLabel = dayStatusLabel(status, locale);

  return (
    <section
      className="rounded-2xl border border-border bg-card/60 p-4"
      data-testid="draft-receipt-card"
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-xl bg-sand/50 grid place-items-center shrink-0">
          <FileText className="h-5 w-5 text-coral" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
            {label}
            {dateLabel ? ` · ${dateLabel}` : ""}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {statusLabel ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                <span className={`h-1.5 w-1.5 rounded-full ${DOT[status]}`} />
                {statusLabel}
              </span>
            ) : null}
          </div>
          <p className="text-sm text-foreground/90 mt-1 line-clamp-2">
            {/* Not started ⇒ no content shown, even if a stale invalidated
                draft row still carries an old headline. But DO show that
                captures are waiting — a captured-but-undrafted day must not
                masquerade as empty. */}
            {restDay
              ? capturedCount > 0
                ? isAr
                  ? `يوم راحة. ${capturedCount === 1 ? "تحديث واحد مُلتقط" : `${capturedCount} تحديثات مُلتقطة`}. تُحتسب ضمن ملخّصك الأسبوعي.`
                  : `Rest day. ${capturedCount} update${capturedCount === 1 ? "" : "s"} captured. They count toward your weekly receipt.`
                : isAr
                  ? "يوم راحة. لا حاجة لملخّص يومي."
                  : "Rest day. No daily receipt needed."
              : notStarted
                ? capturedCount > 0
                  ? isAr
                    ? `${capturedCount === 1 ? "تحديث واحد مُلتقط" : `${capturedCount} تحديثات مُلتقطة`}. جاهز لإنشاء مسودة.`
                    : `${capturedCount} update${capturedCount === 1 ? "" : "s"} captured. Ready to draft.`
                  : isAr
                    ? "لا تحديث بعد. ما تلتقطه يتحوّل إلى مسودة."
                    : "No update yet. Capture turns into a draft."
                : headline || (isAr ? "المسودة جاهزة للمراجعة." : "Draft ready to review.")}
          </p>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        {restDay ? null : notStarted ? (
          <Button onClick={onDraft} disabled={busy} className="rounded-full">
            {busy ? "…" : isAr ? "إنشاء مسودة" : "Draft"}
          </Button>
        ) : approved ? (
          // Approved & unchanged — only Edit. NEVER "Re-approve" (nothing to share).
          // `busy` (drafting OR resolving the receipt row) disables every action
          // button: a button must never look tappable while it can't act.
          <Button variant="outline" onClick={onReview} disabled={busy} className="rounded-full">
            {busy ? "…" : isAr ? "تعديل" : "Edit"}
          </Button>
        ) : updateAvailable ? (
          // Approved + new capture (content still == snapshot). "Review updates"
          // RE-DRAFTS to fold in the new activity, then opens the editor — after
          // which the day is "Changes not shared" with a Re-approve CTA.
          <Button onClick={onDraft} disabled={busy} className="rounded-full">
            {busy ? "…" : isAr ? "مراجعة التحديثات" : "Review updates"}
          </Button>
        ) : changesNotShared || retracted ? (
          // Approved + edited (unshared new version), or retracted ("unshared")
          // — re-approval republishes through the preview.
          <>
            <Button variant="outline" onClick={onReview} disabled={busy} className="rounded-full">
              {busy ? "…" : isAr ? "مراجعة وتعديل" : <>Review &amp; edit</>}
            </Button>
            <Button onClick={onApprove} disabled={busy} className="rounded-full">
              {isPersonal
                ? isAr ? "إعادة الاعتماد" : "Re-approve"
                : isAr ? "إعادة الاعتماد والمشاركة" : <>Re-approve &amp; share</>}
            </Button>
          </>
        ) : (
          // Never approved (draft / ready).
          <>
            <Button variant="outline" onClick={onReview} disabled={busy} className="rounded-full">
              {busy ? "…" : isAr ? "مراجعة وتعديل" : <>Review &amp; edit</>}
            </Button>
            <Button onClick={onApprove} disabled={busy} className="rounded-full">
              {isPersonal
                ? isAr ? "اعتماد" : "Approve"
                : isAr ? "اعتماد ومشاركة" : <>Approve &amp; share</>}
            </Button>
          </>
        )}
        {/* No "Unshare" in a personal workspace — nothing is shared to retract. */}
        {!isPersonal && hasApprovedSnapshot && onUnshare ? (
          <Button
            variant="ghost"
            onClick={onUnshare}
            disabled={busy}
            className="rounded-full text-muted-foreground hover:text-destructive"
            data-testid="receipt-unshare"
          >
            {isAr ? "إلغاء المشاركة" : "Unshare"}
          </Button>
        ) : null}
      </div>

      {restDay ? (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-3">
          <Lock className="h-3.5 w-3.5" />
          <span>
            {isAr
              ? "التحديثات المُلتقطة تبقى خاصة وتُغذّي ملخّصك الأسبوعي."
              : "Captured updates stay private and feed your weekly receipt."}
          </span>
        </div>
      ) : approved ? (
        <div className="flex items-center gap-1 text-xs text-emerald-700 mt-3">
          <CheckCircle2 className="h-3.5 w-3.5" />
          <span>
            {isPersonal
              ? isAr ? "معتمد · خاص بك" : "Approved · Private to you"
              : isAr ? "معتمد · مرئي للفريق" : "Approved · Visible to team"}
          </span>
        </div>
      ) : retracted ? (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-3">
          <Lock className="h-3.5 w-3.5" />
          <span>
            {isPersonal
              ? isAr ? "خاص بك." : "Private to you."
              : isAr
                ? "مسحوب. غير مرئي لفريقك حتى تعيد الاعتماد."
                : "Retracted. Not visible to your team until you re-approve."}
          </span>
        </div>
      ) : hasApprovedSnapshot ? (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-3">
          <Lock className="h-3.5 w-3.5" />
          <span>
            {isPersonal
              ? isAr ? "معتمد. خاص بك." : "Approved. Private to you."
              : isAr
                ? "يرى الفريق نسختك المعتمدة حتى تعيد الاعتماد."
                : "Team sees your approved version until you re-approve."}
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-3">
          <Lock className="h-3.5 w-3.5" />
          <span>
            {isPersonal
              ? isAr ? "خاص بك." : "Private to you."
              : isAr ? "خاص بك حتى تعتمد وتشارك." : <>Private to you until you approve &amp; share.</>}
          </span>
        </div>
      )}
    </section>
  );
}
